import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Send,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { ClubWithCounts } from '../../types';

interface FormData {
  nome: string;
  cognome: string;
  email: string;
  emailConfirm: string;
}

interface ClubConfigurationStatus {
  is_configured: boolean;
  missing_fields: string[];
  message: string;
}

const ReleaseVisitorCardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClubId = searchParams.get('clubId');
  
  const [visitorClubs, setVisitorClubs] = useState<ClubWithCounts[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<string>(preselectedClubId || '');
  const [clubConfigStatus, setClubConfigStatus] = useState<ClubConfigurationStatus | null>(null);
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    cognome: '',
    email: '',
    emailConfirm: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchVisitorClubs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedClubId) {
      checkClubConfiguration(selectedClubId);
    } else {
      setClubConfigStatus(null);
    }
  }, [selectedClubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkClubConfiguration = async (clubId: string) => {
    try {
      const response = await api.get<ClubConfigurationStatus>(`/clubs/${clubId}/configuration-status`);
      setClubConfigStatus(response.data);
    } catch (error) {
      console.error('Failed to check club configuration:', error);
      // Set a default status if the check fails
      setClubConfigStatus({
        is_configured: false,
        missing_fields: ['Unknown'],
        message: 'Could not verify club configuration'
      });
    }
  };

  const fetchVisitorClubs = async () => {
    try {
      setLoadingClubs(true);
      // Fetch all visitor clubs - club managers can see all visitor clubs
      const response = await api.get<ClubWithCounts[]>('/visitor-clubs');
      setVisitorClubs(response.data);
      
      // Check if preselected club exists in visitor clubs
      if (preselectedClubId) {
        const clubExists = response.data.some(club => club.record_id === preselectedClubId);
        if (!clubExists) {
          setError('The selected club is not a visitor club or you do not have access to it.');
          setSelectedClubId('');
        } else {
          // Check if the preselected club is configured
          const selectedClub = response.data.find(club => club.record_id === preselectedClubId);
          if (selectedClub && (!selectedClub.card_front_template || !selectedClub.card_back_template || 
              !selectedClub.sender_email || !selectedClub.front_position)) {
            setError('The selected visitor club is not fully configured. Please select a different club or contact an administrator.');
          }
        }
      } else if (response.data.length > 0) {
        // No preselected club, select the first configured one
        const firstConfigured = response.data.find(club => 
          club.card_front_template && club.card_back_template && 
          club.sender_email && club.front_position
        );
        if (firstConfigured) {
          setSelectedClubId(firstConfigured.record_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch visitor clubs:', error);
      // Fallback to filtering from clubs-with-counts
      try {
        const response = await api.get<ClubWithCounts[]>('/clubs-with-counts');
        const visitorClubsList = response.data.filter(club => club.is_visitor_club === true);
        setVisitorClubs(visitorClubsList);
        
        if (preselectedClubId) {
          const clubExists = visitorClubsList.some(club => club.record_id === preselectedClubId);
          if (!clubExists) {
            setError('The selected club is not a visitor club or you do not have access to it.');
            setSelectedClubId('');
          }
        } else if (visitorClubsList.length > 0) {
          const firstConfigured = visitorClubsList.find(club => 
            club.card_front_template && club.card_back_template && 
            club.sender_email && club.front_position
          );
          if (firstConfigured) {
            setSelectedClubId(firstConfigured.record_id);
          }
        }
      } catch (fallbackError) {
        console.error('Failed to fetch clubs:', fallbackError);
        setError('Failed to load visitor clubs');
      }
    } finally {
      setLoadingClubs(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!selectedClubId) {
      setError('Please select a visitor club');
      return false;
    }
    // Check if club is configured
    if (clubConfigStatus && !clubConfigStatus.is_configured) {
      setError('This visitor club is not fully configured. Please contact an administrator to complete the setup before issuing cards.');
      return false;
    }
    if (!formData.nome.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.cognome.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.email !== formData.emailConfirm) {
      setError('Email addresses do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use the visitor-card endpoint for visitor clubs
      await api.post('/visitor-card', {
        nome: formData.nome.trim(),
        cognome: formData.cognome.trim(),
        email: formData.email.trim(),
        club_id: selectedClubId,
      });

      setSuccess(true);
      
      // Clear form
      setFormData({
        nome: '',
        cognome: '',
        email: '',
        emailConfirm: '',
      });

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/app/visitors');
      }, 3000);
    } catch (error: any) {
      console.error('Failed to release visitor card:', error);
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Check for specific error messages
        if (detail.includes('not fully configured') || detail.includes('missing required templates')) {
          setError('The visitor club is not properly configured. Email and card templates must be set up before cards can be issued.');
        } else if (detail.includes('Club data is not complete')) {
          setError('The visitor club is not properly configured. Please contact an administrator to complete the club setup.');
        } else if (detail.includes('email already exists')) {
          setError('A card has already been issued to this email address.');
        } else if (detail.includes('Failed to send email')) {
          setError('Card was created but email could not be sent. Please check email configuration.');
        } else {
          setError(detail);
        }
      } else if (error.response?.status === 400) {
        setError('Invalid request. The visitor club may not be properly configured.');
      } else if (error.response?.status === 409) {
        setError('A card has already been issued to this email address');
      } else if (error.response?.status === 500) {
        setError('Server error: The visitor club may not be properly configured. Please contact an administrator.');
      } else {
        setError('Failed to release visitor card. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingClubs) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Check if there are any configured visitor clubs
  const configuredClubs = visitorClubs.filter(club => 
    club.card_front_template && 
    club.card_back_template && 
    club.sender_email && 
    club.front_position
  );

  if (visitorClubs.length === 0) {
    return (
      <Container maxWidth="sm">
        <Alert severity="warning" sx={{ mt: 4 }}>
          No visitor clubs available. Please contact an administrator to set up visitor clubs.
        </Alert>
      </Container>
    );
  }

  if (configuredClubs.length === 0) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/app/visitors')}
            sx={{ mb: 2 }}
          >
            Back to Visitor Cards
          </Button>
        </Box>
        <Alert severity="error" sx={{ mt: 4 }}>
          <strong>No Configured Visitor Clubs</strong>
          <br />
          All visitor clubs must be fully configured before cards can be issued.
          <br />
          Please contact an administrator to complete the club configuration with:
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            <li>Card Front Template</li>
            <li>Card Back Template</li>
            <li>Card Front Position</li>
            <li>Sender Email</li>
          </ul>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/app/visitors')}
          sx={{ mb: 2 }}
        >
          Back to Visitor Cards
        </Button>
      </Box>

      <Paper 
        sx={{ 
          p: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3,
            fontWeight: 700,
            background: 'linear-gradient(45deg, #ff00fb 30%, #ff66fc 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Release Visitor Fly Card
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Create a visitor card for temporary access. 
          <strong> Only fully configured clubs can issue cards.</strong>
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Visitor card released successfully! The card has been sent to the provided email address.
            Redirecting...
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {selectedClubId && !error && !success && clubConfigStatus && !clubConfigStatus.is_configured && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Configuration Required:</strong> This visitor club is not fully configured.
            <br />
            <strong>Missing fields:</strong> {clubConfigStatus.missing_fields.join(', ')}
            <br />
            Please contact an administrator to complete the club setup before issuing cards.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Club Selector */}
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Visitor Club</InputLabel>
            <Select
              value={selectedClubId}
              onChange={(e) => setSelectedClubId(e.target.value)}
              label="Visitor Club"
              disabled={loading || success}
            >
              {visitorClubs.map((club) => {
                // Check if this specific club is configured
                const isConfigured = club.card_front_template && 
                                   club.card_back_template && 
                                   club.sender_email && 
                                   club.front_position;
                
                return (
                  <MenuItem 
                    key={club.record_id} 
                    value={club.record_id}
                    disabled={!isConfigured}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ color: !isConfigured ? '#999' : 'inherit' }}>
                          {club.club_name}
                        </span>
                        {!isConfigured && (
                          <Chip 
                            label="Not Configured" 
                            size="small" 
                            color="error"
                            sx={{ height: 20 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip label={`Total: ${club.total_cards}`} size="small" />
                        <Chip label={`Active: ${club.active_cards}`} size="small" color="success" />
                      </Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            name="nome"
            label="First Name"
            value={formData.nome}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            disabled={loading || success}
          />

          <TextField
            name="cognome"
            label="Last Name"
            value={formData.cognome}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            disabled={loading || success}
          />

          <TextField
            name="email"
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            disabled={loading || success}
            helperText="The fly card will be sent to this email address"
          />

          <TextField
            name="emailConfirm"
            label="Confirm Email"
            type="email"
            value={formData.emailConfirm}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            disabled={loading || success}
            error={formData.emailConfirm !== '' && formData.email !== formData.emailConfirm}
            helperText={
              formData.emailConfirm !== '' && formData.email !== formData.emailConfirm
                ? 'Email addresses do not match'
                : 'Please enter the email address again to confirm'
            }
          />

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || success || (clubConfigStatus ? !clubConfigStatus.is_configured : false)}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
              sx={{
                background: 'linear-gradient(45deg, #ff00fb 30%, #ff66fc 90%)',
                boxShadow: '0 3px 5px 2px rgba(255, 0, 251, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #cc00c8 30%, #ff00fb 90%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 10px 4px rgba(255, 0, 251, .3)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                },
              }}
            >
              {loading ? 'Releasing Card...' : 'Release Fly Card'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4, p: 2, backgroundColor: 'rgba(255, 0, 251, 0.05)', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Note:</strong> This action will:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ m: 0, pl: 3 }}>
            <li>Generate a unique fly card for the visitor</li>
            <li>Send the card via email to the provided address</li>
            <li>Store the visitor information in the system</li>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ReleaseVisitorCardPage;